// import * as pdfjsLib from 'pdfjs-dist';

// // pdfjs-dist v5 worker: no default export; use bundler-resolved URL.
// pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
//   'pdfjs-dist/build/pdf.worker.min.mjs',
//   import.meta.url
// ).toString();
import pdfjsLib from '../../../common/functions/pdf_parsing.function';

const normalizeToken = (v) =>
  String(v || '')
    .replace(/\s+/g, '')
    .trim()
    .toLowerCase();

const extractSystemBetween = (text, startRe, endRes = []) => {
  const t = String(text || '');
  const start = t.search(startRe);
  if (start < 0) {
    return '';
  }
  const after = t.slice(start).replace(startRe, '');
  let endIdx = after.length;
  endRes.forEach((re) => {
    const i = after.search(re);
    if (i >= 0) {
      endIdx = Math.min(endIdx, i);
    }
  });
  return after.slice(0, endIdx).replace(/\s+/g, ' ').trim();
};

function mergeSystemWrappedRows(rows) {
  const merged = [];
  for (const row of rows) {
    const rowText = row
      .map((c) => c.str)
      .join(' ')
      .trim();
    if (!rowText) {
      continue;
    }
    // Stop merging once we hit totals/footer; otherwise it can get glued into the last item row.
    if (/notes\s*\/\s*special\s*instructions/i.test(rowText)) {
      break;
    }
    if (/\btotal\s*price\s*:/i.test(rowText)) {
      break;
    }
    const firstToken = String(row[0]?.str || '').trim();
    const startsWithSerial = /^\d+$/.test(firstToken);
    if (startsWithSerial) {
      merged.push([...row]);
    } else if (merged.length > 0) {
      merged[merged.length - 1].push(...row);
      merged[merged.length - 1].sort((a, b) => a.x - b.x);
    } else {
      merged.push([...row]);
    }
  }
  return merged;
}

function rowsSystemoMultilineText(rows) {
  return (rows || [])
    .map((row) =>
      (row || [])
        .map((c) => c.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter(Boolean)
    .join('\n');
}

const formatSystemDateToken = (dateStr) => {
  const s = String(dateStr || '').trim();
  if (!s) {
    return '';
  }
  const m = s.match(/(\d{1,2})-([A-Z]{3})-(\d{2}|\d{4})/i);
  if (m) {
    return `${m[1].padStart(2, '0')}-${m[2].toUpperCase()}-${m[3]}`;
  }
  // fallback: return raw token before time
  return s.split(/\s+/)[0];
};

const dateTokenRe = /(\d{1,2}-[A-Z]{3}-\d{2,4})/i;

const extractDateNearLabel = (text, labelRe) => {
  const t = String(text || '');
  if (!t) {
    return '';
  }
  const idx = t.search(labelRe);
  if (idx < 0) {
    return '';
  }
  // PDF text extraction can reorder tokens (e.g. "Purchase Order Date: Department: 15-FEB-23").
  // So: look for a date token within a small window after the label.
  const after = t.slice(idx).replace(labelRe, '');
  const window = after.slice(0, 120);
  const tok = window.match(dateTokenRe)?.[1];
  return tok ? formatSystemDateToken(tok) : '';
};

const extractOrderToFromTokens = (text) => {
  const tokens = String(text || '')
    .replace(/[^\S\r\n]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  const stopSet = new Set([
    'contactperson',
    'contact',
    'person',
    'address',
    'telephone',
    'tel',
    'email',
    'shipto',
    'onbehalfof',
    'totalprice',
  ]);

  // Find "order" + "to" (or combined) in the token stream.
  let startIdx = -1;
  for (let i = 0; i < tokens.length; i++) {
    const a = normalizeToken(tokens[i]);
    const b = normalizeToken(tokens[i + 1]);
    if (a === 'orderto') {
      startIdx = i + 1;
      break;
    }
    if (a === 'order' && b === 'to') {
      startIdx = i + 2;
      break;
    }
    if (a === 'order' && (b === 'to:' || b === 'to：')) {
      startIdx = i + 2;
      break;
    }
  }
  if (startIdx < 0) {
    return '';
  }

  // Skip optional ":" token or ":" attached
  while (startIdx < tokens.length) {
    const t = normalizeToken(tokens[startIdx]);
    if (t === ':' || t === '：') {
      startIdx += 1;
      continue;
    }
    break;
  }

  const out = [];
  for (let i = startIdx; i < tokens.length; i++) {
    const norm = normalizeToken(tokens[i]);
    if (!norm) {
      continue;
    }
    if (stopSet.has(norm)) {
      break;
    }
    // Stop if token looks like another field label ending with ':'
    if (/:$/.test(tokens[i])) {
      const maybeLabel = normalizeToken(tokens[i].replace(/:$/, ''));
      if (stopSet.has(maybeLabel)) {
        break;
      }
    }
    out.push(tokens[i]);
    if (out.length >= 20) {
      break;
    }
  }
  return out.join(' ').replace(/\s+/g, ' ').trim();
};

const extractFromFirstPageLayout = (items) => {
  const rows = groupSystemItemsIntoRows(items || []);
  const joinRow = (row) =>
    (row || [])
      .map((c) => c.str)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

  const findRowIdx = (re) => rows.findIndex((r) => re.test(joinRow(r)));

  const takeRightSideTextInRow = (row, labelRe) => {
    const labelCell =
      (row || []).find((c) => labelRe.test(String(c.str || ''))) ||
      (row || []).find((c) => /order|purchase|delivery/i.test(String(c.str || ''))) ||
      row?.[0];
    const labelX = Number(labelCell?.x ?? 0);
    const rightCells = (row || []).filter((c) => Number(c.x ?? 0) > labelX + 8);
    return rightCells
      .map((c) => c.str)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const extractDateFromRow = (row, labelRe) => {
    const rowText = joinRow(row);
    const tok = String(rowText).match(dateTokenRe)?.[1];
    if (tok) {
      return formatSystemDateToken(tok);
    }
    const right = takeRightSideTextInRow(row, labelRe);
    const tok2 = String(right).match(dateTokenRe)?.[1];
    return tok2 ? formatSystemDateToken(tok2) : '';
  };

  const extractDateNearRow = (rowIdx, labelRe) => {
    const idx = Number(rowIdx);
    if (!Number.isFinite(idx) || idx < 0) {
      return '';
    }
    // Date tokens sometimes appear a few lines away from the label due to PDF extraction order.
    // Search a wider neighborhood around the label row.
    const start = Math.max(0, idx - 1);
    const end = Math.min(rows.length - 1, idx + 10);
    for (let i = start; i <= end; i++) {
      const tok = String(joinRow(rows[i])).match(dateTokenRe)?.[1];
      if (tok) {
        return formatSystemDateToken(tok);
      }
    }
    // Fallback: right side of starting row
    return extractDateFromRow(rows[idx], labelRe);
  };

  const stopRes = [
    /Contact\s*Person\s*:/i,
    /Address\s*:/i,
    /Telephone\s*:/i,
    /Tel\s*[:：]/i,
    /Email\s*:/i,
    /On\s*Behalf\s*of\s*:/i,
    /Ship\s*To\s*:/i,
    /Total\s*Price\s*:/i,
    /Quantity\s*Unit\s*Price/i,
    /Purchase\s*Order\s*No\s*:/i,
    /\bRev\s*:/i,
    /\bStatus\s*:/i,
  ];

  const extractMultilineBlock = (startRowIdx, startLabelRe) => {
    if (startRowIdx < 0) {
      return '';
    }
    const out = [];
    for (let i = startRowIdx; i < rows.length && out.length < 6; i++) {
      const rowText = joinRow(rows[i]);
      if (!rowText) {
        continue;
      }
      if (i !== startRowIdx && stopRes.some((re) => re.test(rowText))) {
        break;
      }
      if (i === startRowIdx) {
        const right = takeRightSideTextInRow(rows[i], startLabelRe);
        if (right) {
          out.push(right);
        }
      } else {
        out.push(rowText);
      }
    }
    return out
      .join(' ')
      .replace(/\s+/g, ' ')
      .replace(/\bShip\s*To\s*:/gi, '')
      .replace(/\bTotal\s*Price\s*:/gi, '')
      .replace(/\bQuantity\s*Unit\s*Price\b/gi, '')
      .replace(/\bPurchase\s*Order\s*No\s*:/gi, '')
      .replace(/\bRev\s*:/gi, '')
      .replace(/\bStatus\s*:/gi, '')
      .trim();
  };

  const orderToIdx = findRowIdx(/Order\s*To\s*:/i);
  const poDateIdx = findRowIdx(/Purchase\s*Order\s*Date\s*:/i);
  const deliveryIdx = findRowIdx(/Delivery\s*Date\s*:/i);

  return {
    orderTo: extractMultilineBlock(orderToIdx, /Order\s*To\s*:\s*/i),
    purchaseOrderDate:
      poDateIdx >= 0 ? extractDateNearRow(poDateIdx, /Purchase\s*Order\s*Date\s*:\s*/i) : '',
    deliveryDate:
      deliveryIdx >= 0 ? extractDateNearRow(deliveryIdx, /Delivery\s*Date\s*:\s*/i) : '',
  };
};

function groupSystemItemsIntoRows(items) {
  const rowsByY = new Map();
  const yTolerance = 3;

  items.forEach((it) => {
    const x = it.transform?.[4] ?? 0;
    const y = it.transform?.[5] ?? 0;
    const str = String(it.str || '').trim();
    if (!str) {
      return;
    }
    const yKey = Math.round(y / yTolerance) * yTolerance;
    if (!rowsByY.has(yKey)) {
      rowsByY.set(yKey, []);
    }
    rowsByY.get(yKey).push({ str, x, y });
  });

  const ys = [...rowsByY.keys()].sort((a, b) => b - a);
  return ys.map((y) => rowsByY.get(y).sort((a, b) => a.x - b.x));
}

function findSystemHeaderRowWindow(rows) {
  const mustHave = [
    'category',
    'item',
    'type',
    'code',
    'detailed',
    'description',
    'quantity',
    'unit',
    'price',
  ];

  const windows = [1, 2, 3]; // header may span multiple lines
  for (let i = 0; i < rows.length; i++) {
    for (const w of windows) {
      const end = i + w - 1;
      if (end >= rows.length) {
        continue;
      }
      const cells = rows.slice(i, end + 1).flat();
      const text = cells
        .map((c) => c.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .toLowerCase();

      const hitCount = mustHave.filter((k) => text.includes(k)).length;
      const looksLikeHeader =
        text.includes('category') &&
        text.includes('item') &&
        text.includes('description') &&
        text.includes('quantity') &&
        (text.includes('unit price') || (text.includes('unit') && text.includes('price'))) &&
        hitCount >= 7;

      if (looksLikeHeader) {
        // Also capture serial/value if present but not required.
        return {
          startIdx: i,
          endIdx: end,
          headerCells: cells.sort((a, b) => a.x - b.x),
        };
      }
    }
  }
  return null;
}

function buildColumnAnchors(headerRow) {
  const lower = headerRow.map((c) =>
    String(c.str || '')
      .trim()
      .toLowerCase()
  );

  const findSequenceStart = (seq) => {
    for (let i = 0; i <= lower.length - seq.length; i++) {
      let ok = true;
      for (let j = 0; j < seq.length; j++) {
        if (lower[i + j] !== seq[j]) {
          ok = false;
          break;
        }
      }
      if (ok) {
        return i;
      }
    }
    return -1;
  };

  // Anchor x positions by locating header cell starts
  const anchors = {};

  const serialIdx =
    findSequenceStart(['serial', 'no.']) >= 0
      ? findSequenceStart(['serial', 'no.'])
      : lower.findIndex((s) => s.includes('serial') || /^sr\.?\s*no\.?$/i.test(s));
  if (serialIdx >= 0) {
    anchors.serialNo = headerRow[serialIdx].x;
  }

  const categoryIdx = lower.findIndex((s) => s === 'category');
  if (categoryIdx >= 0) {
    anchors.category = headerRow[categoryIdx].x;
  }

  const itemTypeIdx =
    findSequenceStart(['item', 'type']) >= 0
      ? findSequenceStart(['item', 'type'])
      : lower.findIndex((s) => s === 'item type' || normalizeToken(s) === 'itemtype');
  if (itemTypeIdx >= 0) {
    anchors.itemType = headerRow[itemTypeIdx].x;
  }

  const itemCodeIdx =
    findSequenceStart(['item', 'code']) >= 0
      ? findSequenceStart(['item', 'code'])
      : lower.findIndex((s) => s === 'item code' || normalizeToken(s) === 'itemcode');
  if (itemCodeIdx >= 0) {
    anchors.itemCode = headerRow[itemCodeIdx].x;
  }

  const descIdx =
    findSequenceStart(['detailed', 'description']) >= 0
      ? findSequenceStart(['detailed', 'description'])
      : lower.findIndex(
          (s) => s === 'detailed description' || normalizeToken(s) === 'detaileddescription'
        );
  if (descIdx >= 0) {
    anchors.description = headerRow[descIdx].x;
  }

  // "U / M" can be split across 3 cells in some PDFs.
  const uomIdx =
    lower.findIndex((s) => s === 'u/m') >= 0
      ? lower.findIndex((s) => s === 'u/m')
      : lower.findIndex((s) => normalizeToken(s) === 'u/m' || normalizeToken(s) === 'um') >= 0
        ? lower.findIndex((s) => normalizeToken(s) === 'u/m' || normalizeToken(s) === 'um')
        : findSequenceStart(['u', '/', 'm']);
  if (uomIdx >= 0) {
    anchors.uom = headerRow[uomIdx].x;
  }

  const qtyIdx = lower.findIndex((s) => s === 'quantity');
  if (qtyIdx >= 0) {
    anchors.quantity = headerRow[qtyIdx].x;
  }

  const unitPriceIdx = findSequenceStart(['unit', 'price']);
  if (unitPriceIdx >= 0) {
    anchors.unitPrice = headerRow[unitPriceIdx].x;
  } else {
    const priceIdx = lower.findIndex((s) => s === 'price');
    if (priceIdx >= 0) {
      anchors.unitPrice = headerRow[priceIdx].x;
    }
  }

  const valueIdx =
    findSequenceStart(['value', '(pkr)']) >= 0
      ? findSequenceStart(['value', '(pkr)'])
      : lower.findIndex((s) => s.includes('value'));
  if (valueIdx >= 0) {
    anchors.value = headerRow[valueIdx].x;
  }

  return anchors;
}

function pickSystemCellNearX(row, targetX, xTol = 25) {
  if (targetX == null) {
    return null;
  }
  let best = null;
  row.forEach((c) => {
    const dx = Math.abs(c.x - targetX);
    if (dx <= xTol && (!best || dx < best.dx)) {
      best = { ...c, dx };
    }
  });
  return best?.str ?? null;
}

function parseSystemLineItems(rows, headerEndIdx, anchors) {
  const out = [];
  const start = headerEndIdx + 1;

  // Build x-range boundaries between columns so wrapped cells join correctly.
  const colOrder = [
    { key: 'serialNo', x: anchors.serialNo },
    { key: 'category', x: anchors.category },
    { key: 'itemType', x: anchors.itemType },
    { key: 'itemCode', x: anchors.itemCode },
    { key: 'description', x: anchors.description },
    { key: 'uom', x: anchors.uom },
    { key: 'quantity', x: anchors.quantity },
    { key: 'unitPrice', x: anchors.unitPrice },
    { key: 'value', x: anchors.value },
  ]
    .filter((c) => typeof c.x === 'number')
    .sort((a, b) => a.x - b.x);

  const boundaries = colOrder.map((c, idx) => {
    const prev = colOrder[idx - 1];
    const next = colOrder[idx + 1];
    const left = prev ? (prev.x + c.x) / 2 : Number.NEGATIVE_INFINITY;
    const right = next ? (c.x + next.x) / 2 : Number.POSITIVE_INFINITY;
    return { key: c.key, left, right };
  });

  const assignToColumn = (x) => {
    const b = boundaries.find((bb) => x >= bb.left && x < bb.right);
    return b?.key ?? null;
  };

  const joinClean = (arr) => (arr || []).join(' ').replace(/\s+/g, ' ').trim();

  for (let i = start; i < rows.length; i++) {
    const row = rows[i];
    const rowText = row
      .map((c) => c.str)
      .join(' ')
      .trim();
    if (!rowText) {
      continue;
    }

    // stop if we hit footer-ish text
    const lower = rowText.toLowerCase();
    if (lower.includes('total') && (lower.includes('value') || lower.includes('price'))) {
      break;
    }
    if (lower.includes('grand') && lower.includes('total')) {
      break;
    }
    if (lower.includes('purchase order page')) {
      break;
    }

    const colCells = {
      serialNo: [],
      category: [],
      itemType: [],
      itemCode: [],
      description: [],
      uom: [],
      quantity: [],
      unitPrice: [],
      value: [],
    };

    row.forEach((cell) => {
      const key = assignToColumn(cell.x);
      if (!key || !colCells[key]) {
        return;
      }
      colCells[key].push(cell.str);
    });

    const serialNo = joinClean(colCells.serialNo);
    const category = joinClean(colCells.category);
    const itemType = joinClean(colCells.itemType);
    const itemCode = joinClean(colCells.itemCode);
    const description = joinClean(colCells.description) || rowText;
    const uom = joinClean(colCells.uom);
    const quantity = joinClean(colCells.quantity);
    const unitPrice = joinClean(colCells.unitPrice);
    const value = joinClean(colCells.value);

    // ignore rows that are clearly not data (e.g. repeated header)
    const hasAny =
      itemType || category || itemCode || (quantity && quantity !== 'Quantity') || unitPrice;
    if (!hasAny) {
      continue;
    }

    // Hard filter: for this PO format, valid rows have numeric item codes and numeric quantities.
    const serialToken = String(serialNo || '').match(/\d+/)?.[0] || '';
    const serialOk = Boolean(serialToken);
    const itemCodeIsOk = itemCode ? /^\d{4,}$/.test(String(itemCode).replace(/\s+/g, '')) : false;
    const qtyIsOk = quantity ? /^\d+$/.test(String(quantity).replace(/\s+/g, '')) : false;
    if (!serialOk || !itemCodeIsOk || !qtyIsOk) {
      continue;
    }

    out.push({
      serialNo: serialToken || serialNo || '',
      itemType: itemType || '',
      category: category || '',
      itemCode: itemCode || '',
      description: description || '',
      uom: uom || '',
      quantity: quantity || '',
      unitPrice: unitPrice || '',
      value: value || '',
    });
  }

  return out;
}

function parseSystemLineItemsByRowPattern(rows, headerEndIdx) {
  const out = [];

  const joinRow = (row) =>
    (row || [])
      .map((c) => c.str)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

  const itemTypeAlternation =
    '(?:SERVICE ITEMS|INVENTORY\\s*\\((?:NON-?\\s*)?SERIALIZE\\s*D\\)|INVENTORY\\s*\\((?:NON-?\\s*)?SERIALIZED\\)|TECHNICAL\\s*\\((?:NON-?\\s*)?SERIALIZE\\s*D\\)|TECHNICAL\\s*\\((?:NON-?\\s*)?SERIALIZED\\)|INVENTORY|TECHNICAL)';

  const uomAlternation =
    '(?:Job|Each|Meter|Set|Lot|Day|Month|Year|Hour|Hours|Nos\\.?|No\\.?|Number|Piece|Pcs\\.?|EA|Unit)';

  const moneyToken = '[\\d,]+(?:\\.(?:\\s*\\d{1,2}\\s+\\d{1,3}(?=\\s|$)|\\s*\\d+))?';

  const rowRe = new RegExp(
    `^(\\d+)\\s+(?:\\d+\\s+)?(.+?)\\s+(?:(${itemTypeAlternation})\\s+)?(\\d{4,})\\s+(.+?)\\s+(${uomAlternation})\\s+(\\d+(?:\\.\\d+)?)\\s+(${moneyToken})(?:\\s+(${moneyToken}))?(?:\\s+.*)?$`,
    'i'
  );

  const cleanMoney = (value) =>
    String(value || '')
      .replace(/\.\s+/g, '.')
      .replace(/\s+/g, '')
      .trim();

  // Track rows already consumed by multi-row buffering.
  const consumedSet = new Set();

  for (let i = headerEndIdx + 1; i < rows.length; i++) {
    if (consumedSet.has(i)) {
      continue;
    }

    const rowText0 = joinRow(rows[i]);
    if (!rowText0) {
      continue;
    }

    // Must start with a digit (serial number) to be a potential item row.
    // Use (\s|$) instead of \s+ so a lone serial like "10" (no space) also passes.
    if (!/^\d+(\s|$)/.test(rowText0)) {
      continue;
    }

    // Try direct single-row match first.
    let m = rowText0.match(rowRe);
    let matchedUpTo = i;

    if (!m) {
      // Buffer up to 8 following rows to reconstruct a wrapped item row.
      // Only stop when the next row's serial number differs (new item).
      const curSerial = (rowText0.match(/^(\d+)/) || [])[1] || '';
      let buffer = rowText0;
      for (let la = 1; la <= 8 && i + la < rows.length; la++) {
        if (consumedSet.has(i + la)) {
          continue;
        }
        const nextText = joinRow(rows[i + la]);
        if (!nextText) {
          continue;
        }
        // Don't absorb totals/footer into the last row.
        if (
          /\btotal\s*price\s*:/i.test(nextText) ||
          /notes\s*\/\s*special\s*instructions/i.test(nextText)
        ) {
          break;
        }
        if (/^\d+(\s|$)/.test(nextText)) {
          const nextSerial = (nextText.match(/^(\d+)/) || [])[1] || '';
          if (nextSerial !== curSerial) {
            break;
          }
        }
        buffer = (buffer + ' ' + nextText).replace(/\s+/g, ' ').trim();
        const mm = buffer.match(rowRe);
        if (mm) {
          m = mm;
          matchedUpTo = i + la;
          break;
        }
      }
    }

    if (!m) {
      continue;
    }

    for (let k = i + 1; k <= matchedUpTo; k++) {
      consumedSet.add(k);
    }

    const qtyRaw = String(m[7] || '').trim();
    const qtyNum = Number(qtyRaw);
    const money1 = String(m[8] || '').trim();
    const money2 = String(m[9] || '').trim();

    const unitPrice = cleanMoney(money1);
    const value = cleanMoney(money2 || (Number.isFinite(qtyNum) && qtyNum === 1 ? money1 : ''));

    out.push({
      serialNo: m[1],
      category: m[2],
      itemType: String(m[3] || '')
        .replace(/\s+/g, ' ')
        .toUpperCase(),
      itemCode: m[4],
      description: m[5],
      uom: m[6],
      quantity: qtyRaw,
      unitPrice,
      value,
    });
  }

  return out.filter((r) => r.serialNo && r.itemCode && r.quantity);
}

function heuristicSystemParseLineItemsFromRawText(rawText) {
  const lines = String(rawText || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const takeBetween = (startRe, endRe) => {
    const startIdx = lines.findIndex((l) => startRe.test(l));
    if (startIdx < 0) {
      return [];
    }
    const slice = lines.slice(startIdx + 1);
    const endIdx = slice.findIndex((l) => endRe.test(l));
    return (endIdx >= 0 ? slice.slice(0, endIdx) : slice).filter(Boolean);
  };

  // Descriptions usually appear before the "Category" block.
  const descriptionLines = takeBetween(/^\d+\.\d+|\bU\s*\/\s*M\b/i, /^Category$/i).filter((l) =>
    /[A-Za-z]/.test(l)
  );

  const categoryLines = takeBetween(/^Category$/i, /^\d{1,2}-[A-Z]{3}-\d{2,4}$/i).filter((l) =>
    /[A-Za-z]/.test(l)
  );

  // Item codes + item types appear under "Item Type Item Code" on many PDFs.
  const itemBlock = takeBetween(/^Item Type Item Code$/i, /^--\s*\d+\s*of\s*\d+\s*--/i);
  const itemCodes = itemBlock.filter((l) => /^\d{5,}$/.test(l));
  const itemTypes = itemBlock.filter((l) => /[A-Za-z]/.test(l) && !/^\d/.test(l));

  // Quantity / Unit price often appear in a block with "Quantity Unit Price".
  const qtyPriceBlock = takeBetween(/^Quantity Unit Price$/i, /^Category$/i);
  const numbers = qtyPriceBlock.join(' ').match(/[\d,]+\.\d{2}|\d+/g);

  // Very loose: we can't reliably split quantity vs unitPrice for all formats, so keep blanks if unclear.
  // Prefer building rows from what we *can* align: itemCode + itemType + description + category.
  const n =
    Math.max(itemCodes.length, itemTypes.length, descriptionLines.length, categoryLines.length) ||
    0;
  const out = [];
  for (let i = 0; i < n; i++) {
    out.push({
      itemType: itemTypes[i] || '',
      category: categoryLines[i] || '',
      itemCode: itemCodes[i] || '',
      description: descriptionLines[i] || '',
      quantity: '',
      unitPrice: '',
    });
  }
  return out.filter((r) => Object.values(r).some((v) => String(v).trim()));
}

export async function parseSystemPoPdf(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  let fullText = '';
  let pageTextForItems = '';
  let tableRowsForItems = null;
  let firstPageText = '';
  let firstPageItems = null;

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items.map((it) => it.str).join(' ');
    fullText += pageText + '\n';
    if (i === 1) {
      firstPageText = pageText;
      firstPageItems = textContent.items || [];
    }

    // Try to detect the *actual line-items table page* (screenshot shows header on page 1).
    // We only parse line items from the page that contains the table header to avoid picking up
    // "GENERAL CONDITIONS..." text from page 2/3 and 3/3.
    // Detect the page with the table header, then also collect rows from subsequent pages
    // in case the table spans multiple pages (e.g. 10th item on page 2).
    if (!tableRowsForItems) {
      const rows = groupSystemItemsIntoRows(textContent.items);
      const headerWindow = findSystemHeaderRowWindow(rows);
      if (headerWindow) {
        const headerPart = rows.slice(0, headerWindow.endIdx + 1);
        const mergedData = mergeSystemWrappedRows(rows.slice(headerWindow.endIdx + 1));
        tableRowsForItems = [...headerPart, ...mergedData];
        pageTextForItems = rowsSystemoMultilineText(tableRowsForItems);
      }
    } else {
      // Already found the header — append rows from this continuation page.
      // Stop appending if this page is clearly a "General Conditions" / notes-only page.
      const pageTextLower = pageText.toLowerCase();
      const looksLikeConditionsPage =
        pageTextLower.includes('general conditions') ||
        pageTextLower.includes('terms and conditions');
      if (!looksLikeConditionsPage) {
        const continuationRows = groupSystemItemsIntoRows(textContent.items);
        const mergedContinuation = mergeSystemWrappedRows(continuationRows);
        tableRowsForItems = [...tableRowsForItems, ...mergedContinuation];
        pageTextForItems += '\n' + rowsSystemoMultilineText(mergedContinuation);
      }
    }
  }

  const extracted = {
    purchaseOrderNo: '',
    poNo: '',
    orderTo: '',
    purchaseOrderDate: '',
    deliveryDate: '',
    lineItems: [],
    rawText: fullText,
  };

  const extractPoNumber = (text) => {
    const t = String(text || '');
    if (!t) {
      return '';
    }

    // Be tolerant: pdf text extraction can reorder tokens or drop/alter punctuation.
    // Handle:
    // - "PO No : 6857" / "PO No: 6857" / "PO No 6857"
    // - "PO No : ____ 6857" (some PDFs represent underline as underscores/dashes)
    // - "6857 PO No :" (number before label)
    // - "Purchase Order No: 6857"
    const candidates = [
      /(?:^|\s)(\d{3,})\s*(?:PO|P\.?O\.?)\s*No\s*[:：]?(?:\s|$)/i,
      /(?:PO|P\.?O\.?)\s*No\s*[:：]?\s*[_\-\u2013\u2014]*\s*(\d{3,})/i,
      /Purchase\s*Order\s*No\s*[:：]?\s*[_\-\u2013\u2014]*\s*(\d{3,})/i,
    ];

    for (const re of candidates) {
      const m = t.match(re);
      if (m?.[1]) {
        return String(m[1]).trim();
      }
    }

    // Last resort: scan token-by-token near "PO" + "No"
    const tokens = t
      .replace(/[^\S\r\n]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean);
    for (let i = 0; i < tokens.length; i++) {
      const a = normalizeToken(tokens[i]);
      const b = normalizeToken(tokens[i + 1]);
      const c = normalizeToken(tokens[i + 2]);
      const isPo =
        a === 'po' || a === 'p.o' || a === 'p.o.' || a === 'pono' || a === 'po#' || a === 'po:';
      const isNo = b === 'no' || b === 'no:' || b === 'no.' || b === 'no:' || b === 'no：';
      if (isPo && isNo) {
        const next = tokens.slice(i + 2, i + 8).join(' ');
        const mm = next.match(/(\d{3,})/);
        if (mm?.[1]) {
          return String(mm[1]).trim();
        }
      }
      // Sometimes token is "PO" then "No:" attached.
      if ((a === 'po' || a === 'p.o' || a === 'p.o.') && (b === 'no:' || b === 'no：')) {
        const next = tokens.slice(i + 2, i + 8).join(' ');
        const mm = next.match(/(\d{3,})/);
        if (mm?.[1]) {
          return String(mm[1]).trim();
        }
      }
      // Sometimes token is "PO" then "No" then ":" as separate token.
      if ((a === 'po' || a === 'p.o' || a === 'p.o.') && b === 'no' && (c === ':' || c === '：')) {
        const next = tokens.slice(i + 3, i + 9).join(' ');
        const mm = next.match(/(\d{3,})/);
        if (mm?.[1]) {
          return String(mm[1]).trim();
        }
      }
    }
    return '';
  };

  // Prefer page-1 for PO number (avoids matching other numbers in conditions pages).
  const poFromFirstPage = extractPoNumber(firstPageText);
  const poFromFull = poFromFirstPage ? '' : extractPoNumber(fullText);
  const poNo = poFromFirstPage || poFromFull;
  if (poNo) {
    extracted.purchaseOrderNo = poNo;
    extracted.poNo = poNo;
  }

  const firstPageForFields = firstPageText || fullText;

  // Extract Order To from page-1 header area, stop before Ship To / On Behalf of
  extracted.orderTo =
    extractSystemBetween(firstPageForFields, /Order\s*To\s*:\s*/i, [
      /Contact\s*Person\s*:/i,
      /Address\s*:/i,
      /Telephone\s*:/i,
      /Tel\s*[:：]/i,
      /Email\s*:/i,
      /On\s*Behalf\s*of\s*:/i,
      /Ship\s*To\s*:/i,
      /Total\s*Price\s*:/i,
    ]) ||
    extractOrderToFromTokens(firstPageForFields) ||
    '';

  // Prefer first page for header fields to avoid picking up dates from other sections/pages.
  extracted.purchaseOrderDate =
    extractDateNearLabel(firstPageForFields, /Purchase\s*Order\s*Date\s*:\s*/i) || '';

  extracted.deliveryDate =
    extractDateNearLabel(firstPageForFields, /Delivery\s*Date\s*:\s*/i) || '';

  // Layout-based override (more reliable than raw text when pdf extraction reorders tokens)
  if (firstPageItems && firstPageItems.length) {
    const layout = extractFromFirstPageLayout(firstPageItems);
    if (layout.orderTo) {
      extracted.orderTo = layout.orderTo;
    }
    if (layout.purchaseOrderDate) {
      extracted.purchaseOrderDate = layout.purchaseOrderDate;
    }
    if (layout.deliveryDate) {
      extracted.deliveryDate = layout.deliveryDate;
    }
  }

  // Final cleanup for Order To (remove accidental trailing header labels)
  if (extracted.orderTo) {
    extracted.orderTo = String(extracted.orderTo)
      .replace(/\bOn\s*Behalf\s*of\s*:.*$/i, '')
      .replace(/\bPurchase\s*Order\s*No\s*:.*$/i, '')
      .replace(/\bRev\s*:\s*\d+\b.*$/i, '')
      .trim();
  }

  if (tableRowsForItems) {
    const headerWindow = findSystemHeaderRowWindow(tableRowsForItems);
    if (headerWindow) {
      const anchors = buildColumnAnchors(headerWindow.headerCells);
      const rowPatternItems = parseSystemLineItemsByRowPattern(
        tableRowsForItems,
        headerWindow.endIdx
      );
      const coordinateItems = parseSystemLineItems(tableRowsForItems, headerWindow.endIdx, anchors);
      // Short POs often parse best with the strict row regex, but multi-page POs can split
      // item type/description across visual rows. Keep whichever parser recovers more items.
      extracted.lineItems =
        coordinateItems.length > rowPatternItems.length ? coordinateItems : rowPatternItems;
    }
  }

  // Fallback for cases where the header exists but positional parsing still fails.
  // IMPORTANT: only fallback using the page that contains the header, not the whole PDF.
  if ((!extracted.lineItems || extracted.lineItems.length === 0) && pageTextForItems) {
    const heuristic = heuristicSystemParseLineItemsFromRawText(pageTextForItems);
    // Heuristic parsing is noisy for this PO; only accept if it looks like real rows.
    const looksLikeRows =
      Array.isArray(heuristic) &&
      heuristic.length > 0 &&
      heuristic.some((r) => /^\d+$/.test(String(r?.serialNo || '').trim()));
    extracted.lineItems = looksLikeRows ? heuristic : [];
  }

  return extracted;
}

export function normalizeSystemPoForCompare(po) {
  const raw = String(po || '').trim();
  if (!raw) {
    return '';
  }
  const digits = raw.match(/\d+/g)?.join('') || '';
  if (digits) {
    return digits;
  }
  return normalizeToken(raw).replace(/[^a-z0-9]/g, '');
}

// Backwards-compatible alias (older imports expect this name).
export const normalizePoForCompare = normalizeSystemPoForCompare;
